import { useState, useCallback } from 'react';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import { COMPANY } from '@/creditflowresearch/application/PageFiltersState/constants';
import { preparePayload } from '@/creditflowresearch/application/Deal/helpers/prepareDealsPayload';
import { IScorecardProps, TUpdateFiltersPayload } from '@/creditflowresearch/application/League/Scorecard/types';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';

export const useScorecardFilters = (
  props: IScorecardProps,
  activeProductIds: string[],
  activeRegionIds: string[],
) => {
  const { setAdditionalFilters } = usePageFiltersContext();
  const { plan } = useUserPlanContext();

  const [scorecardFilters, setScorecardFilters] = useState<Record<string, unknown>>(() => {
    const { filters, ...rest } = props.filtersByProduct;
    const baseFilters = {
      ...rest,
      getDealsBy: COMPANY,
    } as any;

    const { filters: preparedFilters, ...preparedRest } = preparePayload(baseFilters, { page: 1, userPlan: plan });
    return {
      ...preparedRest,
      filters: {
        ...props.filtersByProduct.filters,
        dateFrom: preparedFilters.dateFrom,
        dateTo: preparedFilters.dateTo,
        includedSectorId: preparedFilters.includedSectorId,
        includedSubsectorId: preparedFilters.includedSubsectorId,
        includedIndustryIds: preparedFilters.includedIndustryId ? [preparedFilters.includedIndustryId] : [],
        includedSubindustryIds: preparedFilters.includedSubindustryId ? [preparedFilters.includedSubindustryId] : [],
        includedProductIds: baseFilters.includedProductIds || [],
        includedRegionIds: baseFilters.includedRegionIds || [],
        includedUnderwriterIds: (props?.filtersByProduct?.filters as any)?.includedUnderwriterIds,
        includedRatingAgencyIds: (props?.filtersByProduct?.filters as any)?.includedRatingAgencyIds,
      },
    };
  });

  const updateFilters = useCallback((additionalFilters: TUpdateFiltersPayload) => {
    const { filters: propsFilters, ...propsRest } = props.filtersByProduct;
    const mergedFilters = {
      ...propsRest,
      ...additionalFilters,
      getDealsBy: COMPANY,
    };

    const { filters, ...rest } = preparePayload(mergedFilters, { page: 1, userPlan: plan });
    const syncedPayload = {
      ...rest,
      filters: {
        ...props.filtersByProduct.filters,
        includedUnderwriterIds: (props?.filtersByProduct?.filters as any)?.includedUnderwriterIds,
        includedRatingAgencyIds: (props?.filtersByProduct?.filters as any)?.includedRatingAgencyIds,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        includedSectorId: filters.includedSectorId,
        includedSubsectorId: filters.includedSubsectorId,
        includedIndustryIds: filters.includedIndustryId ? [filters.includedIndustryId] : [],
        includedSubindustryIds: filters.includedSubindustryId ? [filters.includedSubindustryId] : [],
        includedProductIds: activeProductIds,
        includedRegionIds: activeRegionIds,
      },
    };

    setScorecardFilters(syncedPayload);
    setAdditionalFilters(syncedPayload);

    return syncedPayload;
  }, [setAdditionalFilters, props.filtersByProduct, activeProductIds, activeRegionIds]);

  return {
    scorecardFilters,
    updateFilters,
  };
};
